import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Modal } from 'antd';

import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic';
import { useHistory } from 'react-router-dom';
import RoutePath from '../../../RoutePath';
import { LanguageService as i18n } from '../../../services';
import {
  createTelematicAccount,
  submitUnknownTelematicProvider,
  FeatureToggle,
} from '../../../store/actions';
import { getSelectedCompany, getTelematicAccountFormConfig, isFeatureEnabled } from '../../../store/selectors';
import { ERROR_CODE } from '../../../utils/apiError';
import {
  isError, isSuccess, getErrorCode, getPayloadData,
} from '../../../utils/responseUtils';
import { uuidv4 } from '../../../utils/uuid';
import TelematicAccountForm from '../Components/TelematicAccountForm';
import { TelematicAccountOperationType } from '../../../types/enums/telematicAccountFormViewType.enum';

export const submittedVinsSuccessModal = ({
  payloadData, telematicAccountFormConfig, title, content, goToDetailsPage, isAddVehiclesOperation,
}) => {
  if (payloadData?.providerAccount?.credentials?.vins?.details?.lastSubmitted) {
    const {
      providerAccount: { providerId: responseProviderId, credentials: { vins: { details: { lastSubmitted } } } },
    } = payloadData;
    const providerName = telematicAccountFormConfig
      .find(({ providerId }) => providerId === responseProviderId).displayName;
    if (lastSubmitted?.length > 0) {
      return {
        title: title.concat(isAddVehiclesOperation ? '.CREDENTIALS_SUBMITTED' : '.DEFAULT'),
        content: i18n.t(content.concat('.VEHICLES_SUBMITTED'), { vehiclesCount: lastSubmitted.length, providerName }),
        constructor: Modal.success,
        onOk: goToDetailsPage,
        okText: 'TELEMATICS.ACCOUNT.BUTTON_GO_TO_DETAILS_PAGE',
      };
    }
  }
  return undefined;
};

const TelematicAccountCreationPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const [accountCreationForm] = Form.useForm();

  const contextCompanyId = useSelector(getSelectedCompany);
  const { telematicAccountFormConfig } = useSelector(getTelematicAccountFormConfig);
  const createAnywayEnabled = useSelector(isFeatureEnabled(FeatureToggle.TELEMATIC_ACCOUNT_CREATE_ANYWAY_ENABLED));

  const [submitClicked, setSubmitClicked] = useState(false);
  const [generatedCredentials, setGeneratedCredentials] = useState(null);
  const redirectToPathLocalStorageKey = (path) => `REDIRECT_TO_${path}`;

  const confirmationModalKeysAndConstructor = (
    response, isUnknownProvider, handleUserNeedsSupport, isPushIngestionMode,
  ) => {
    const forSuccess = isSuccess(response);
    const forFailure = isError(response);
    const statusSuffix = (forSuccess && 'SUCCESS') || (forFailure && 'ERROR') || 'STARTED';
    const typeInterfix = isUnknownProvider ? 'UNKNOWN_PROVIDER' : 'NEW_ACCOUNT';
    const title = `TELEMATICS.ACCOUNT.CREATE.CONFIRMATION.TITLE.${typeInterfix}.${statusSuffix}`;
    const content = `TELEMATICS.ACCOUNT.CREATE.CONFIRMATION.CONTENT.${typeInterfix}.${statusSuffix}`;

    const createAnywayOrNeedSupportProps = createAnywayEnabled
      ? {
        supportsForceCreate: true,
        okText: 'TELEMATICS.ACCOUNT.BUTTON_CREATE_ANYWAY',
        cancelText: 'COMMON.CANCEL',
      }
      : {
        needsSupport: true,
        okText: 'TELEMATICS.ACCOUNT.BUTTON_NEED_SUPPORT',
        cancelText: 'COMMON.CANCEL',
      };

    if (forSuccess) {
      const payloadData = getPayloadData(response);
      const accountDetailsRoutePath = RoutePath.telematicAccountDetails(payloadData.providerAccount?.accountId);
      const goToDetailsPage = () => {
        localStorage.removeItem(redirectToPathLocalStorageKey(accountDetailsRoutePath));
        history.push(accountDetailsRoutePath);
      };
      const goBackToTelematicsMain = () => (history.push(RoutePath.telematicAccountsView));

      const successModal = submittedVinsSuccessModal({
        payloadData, telematicAccountFormConfig, title, content, goToDetailsPage,
      });
      if (successModal) {
        return { ...successModal, onOkRoutePath: accountDetailsRoutePath };
      }
      // PUSH-mode providers generate credentials on account creation. The user
      // needs to stay on the form to copy them, so we don't navigate away on OK
      // and we surface a different title/content pair.
      if (isPushIngestionMode && !isUnknownProvider) {
        return {
          title: title.concat('.PUSH'),
          content: (
            <>
              {i18n.t(content.concat('.PUSH'))}
            </>
          ),
          constructor: Modal.success,
          onOk: () => {},
          okText: 'COMMON.OK',
          onOkRoutePath: accountDetailsRoutePath,
        };
      }
      return {
        title: title.concat('.DEFAULT'),
        content: (
          <>
            {i18n.t(content.concat('.DEFAULT'))}
          </>
        ),
        constructor: isUnknownProvider ? Modal.warning : Modal.success,
        onOk: isUnknownProvider ? goBackToTelematicsMain : goToDetailsPage,
        okText: isUnknownProvider ? 'COMMON.OK' : 'TELEMATICS.ACCOUNT.BUTTON_GO_TO_DETAILS_PAGE',
        onOkRoutePath: accountDetailsRoutePath,
      };
    }

    const errorCode = getErrorCode(response);
    switch (errorCode) {
      case ERROR_CODE.SCANIA_VAT_NUMBER_NOT_FOUND:
      case ERROR_CODE.CONNECTIVITY_AGREEMENT_NOT_SIGNED:
      case ERROR_CODE.IDEM_VAT_NUMBER_AND_VINS_NOT_MATCHED:
      case ERROR_CODE.KOEGEL_VAT_NUMBER_AND_VINS_NOT_MATCHED:
      case ERROR_CODE.SCHWARZMULLER_VAT_NUMBER_AND_VINS_NOT_MATCHED:
        return {
          title: title.concat(`.${errorCode}`),
          content: i18n.t(content.concat('.CLICK_I_NEED_SUPPORT')),
          constructor: Modal.error,
          width: 463,
          onOk: handleUserNeedsSupport,
          okText: 'TELEMATICS.ACCOUNT.BUTTON_NEED_SUPPORT',
        };
      case ERROR_CODE.TELEMATIC_PROVIDER_ACCOUNT_EXISTS:
        return {
          title: title.concat('.DEFAULT'),
          content: i18n.t(content.concat(`.${errorCode}`)),
          constructor: Modal.error,
          width: 463,
          ...createAnywayOrNeedSupportProps,
        };
      case ERROR_CODE.PROVIDER_ACCOUNT_NO_DEVICES:
        return {
          title: title.concat(`.${errorCode}`),
          content: i18n.t(content.concat(`.${errorCode}`)),
          constructor: Modal.error,
          width: 463,
          ...createAnywayOrNeedSupportProps,
        };
      default:
        return {
          title: title.concat('.DEFAULT'),
          content: i18n.t(content.concat('.DEFAULT')),
          constructor: forFailure ? Modal.error : Modal.info,
          ...(forFailure ? createAnywayOrNeedSupportProps : {}),
        };
    }
  };

  const dispatchTelematicAccountOrUnknownProvider = ({ payload, isUnknownProvider, handleUserNeedsSupport }) => {
    if (submitClicked) {
      return;
    }
    setSubmitClicked(true);
    const action = isUnknownProvider
      ? submitUnknownTelematicProvider
      : createTelematicAccount;
    const selectedProviderConfig = telematicAccountFormConfig
      ?.find(({ providerId }) => providerId === payload.providerId);
    const isPushIngestionMode = selectedProviderConfig?.dataIngestionMode === 'PUSH';
    dispatch(action(payload, contextCompanyId))
      .then((response) => {
        const modalInfo = confirmationModalKeysAndConstructor(
          response,
          isUnknownProvider,
          handleUserNeedsSupport,
          isPushIngestionMode,
        );
        setSubmitClicked(false);

        // Populate autocomplete-driven fields (e.g. generated credentials returned by
        // the backend on PUSH-mode providers) so TelematicAccountDetailsSection can
        // render them via the `autocomplete + non-empty value` branch.
        // Setting `generatedCredentials` triggers a re-render of the form subtree
        // so the details section re-evaluates which autocomplete fields to show.
        if (isSuccess(response)) {
          const credentials = getPayloadData(response)?.providerAccount?.credentials;
          if (credentials && typeof credentials === 'object') {
            accountCreationForm.setFieldsValue(credentials);
            setGeneratedCredentials(credentials);
          }
        }

        let onOk;
        if (modalInfo.supportsForceCreate) {
          onOk = () => {
            dispatch(createTelematicAccount({ ...payload, forceCreate: true }, contextCompanyId))
              .then((forceResponse) => {
                const forcePayloadData = getPayloadData(forceResponse);
                const accountDetailsRoutePath = RoutePath.telematicAccountDetails(
                  forcePayloadData.providerAccount?.accountId,
                );
                history.push(accountDetailsRoutePath);
              });
          };
        } else if (modalInfo.needsSupport) {
          onOk = handleUserNeedsSupport;
        } else {
          onOk = modalInfo.onOk;
        }

        const modalType = modalInfo.constructor;
        const isSuccessModal = modalType === Modal.success;
        const showCancel = modalInfo.supportsForceCreate || modalInfo.needsSupport;
        const modal = modalType({
          key: uuidv4(),
          title: i18n.t(modalInfo.title),
          content: modalInfo.content,
          onOk,
          okText: i18n.t(modalInfo.okText),
          centered: true,
          width: modalInfo.width,
          ...(showCancel ? { okCancel: true, cancelText: i18n.t(modalInfo.cancelText) } : {}),
          ...(isSuccessModal ? { keyboard: false } : { closable: true, maskClosable: true }),
        });
        if (isSuccessModal && !isPushIngestionMode) {
          localStorage.setItem(redirectToPathLocalStorageKey(modalInfo.onOkRoutePath), true);
          setTimeout(() => {
            if (localStorage.getItem(redirectToPathLocalStorageKey(modalInfo.onOkRoutePath))) {
              modalInfo.onOk();
              modal.destroy();
            }
          }, 5000);
        }
      });
  };

  return (
    <>
      <BreadcrumbsItem to={RoutePath.telematicAccountsView}>{i18n.t('BREADCRUMB.TELEMATICS')}</BreadcrumbsItem>
      <BreadcrumbsItem to={RoutePath.telematicAccountsCreate}>
        {i18n.t('BREADCRUMB.TELEMATICS_NEW_ACCOUNT')}
      </BreadcrumbsItem>

      <TelematicAccountForm
        form={accountCreationForm}
        submitDispatchFunction={dispatchTelematicAccountOrUnknownProvider}
        creationView
        operationType={TelematicAccountOperationType.CREATE}
        submitClicked={submitClicked}
        setSubmitClicked={setSubmitClicked}
        generatedCredentials={generatedCredentials}
      />
    </>
  );
};

TelematicAccountCreationPage.propTypes = {};

TelematicAccountCreationPage.defaultProps = {};

export default TelematicAccountCreationPage;
